import { useState } from "react";
import { useParams, useSearch } from "wouter";
import { useNavegacao } from "@/lib/navegacao";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ArrowLeft, Check, Send, Pencil, FileText, Image, MessageCircle, MoreHorizontal, RotateCcw, Ban, Trash2,
  Handshake, CircleAlert, Copy, Undo2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth-context";
import { usePode } from "@/lib/permissoes";
import { fetchJson } from "@/lib/queryClient";
import {
  useVenda, useRateioVenda, useEmitirVenda, useDespagarVenda, useCancelarVenda, useExcluirVenda,
  podeEditarVenda,
} from "@/hooks/use-vendas";
import { cargaEmPesagem } from "@shared/vendas-itens";
import { ModalFormalizar } from "@/components/mercado/formalizar-comprador";
import { LinhaDoTempoVenda } from "@/components/mercado/linha-do-tempo-venda";
import { HistoricoDaVenda } from "@/components/mercado/historico-da-venda";
import { ChipDinheiro } from "@/components/mercado/dinheiro-chip";
import { estadoDinheiroDaVenda, linhaDoTempoDaVenda } from "@shared/mercado";
import { hojeNaFazenda } from "@/lib/data-fazenda";
import { SeletorVencimento } from "@/components/mercado/seletor-vencimento";
import { DialogRecebi } from "@/components/mercado/dialog-recebi";
import {
  useCompartilhamentoDaVenda, useDesfazerEnvio, useEmitirEConfirmar, useMarcarEnviado, useMensagemCompartilhamento,
  useOrigemDaVenda,
} from "@/hooks/use-mercado";
import { DialogNaoRecebi } from "@/components/mercado/dialog-nao-recebi";
import { ConferenciaDaVenda } from "@/components/mercado/conferencia-da-venda";
import { DialogEmitirEConfirmar, mensagemDeErroDaEmissao } from "@/components/mercado/dialog-emitir-e-confirmar";

import { PageHeader } from "@/components/layout/page-header";
import { PageSkeleton } from "@/components/data/page-skeleton";
function fmtMoney(v: number | string) {
  return `R$ ${parseFloat(String(v)).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtKg(v: number | string) {
  return `${parseFloat(String(v)).toLocaleString("pt-BR", { maximumFractionDigits: 0 })} kg`;
}
/** Número no padrão BR: "1,40" com `minimo` 2; "18,5" ou "1" com `minimo` 0. */
function fmtDecimal(v: number | string, minimo: number) {
  return parseFloat(String(v)).toLocaleString("pt-BR", { minimumFractionDigits: minimo, maximumFractionDigits: 2 });
}
function fmtKgPorCacho(v: number) {
  return v.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}
function fmtData(v: string) {
  return new Date(v + "T00:00:00").toLocaleDateString("pt-BR");
}
// Cachos alocados são FRACIONÁRIOS (fração de rateio, não contagem física). O arredondamento
// acontece SÓ aqui, na renderização: arredondar antes de somar quebraria a conservação.
function fmtCachos(v: number) {
  return v.toLocaleString("pt-BR", { maximumFractionDigits: 1 });
}
function fmtDataHora(iso: string) {
  const d = new Date(iso);
  return `${d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })} ${d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`;
}
function pctAlocado(l: { cachosAlocados: number; cachosGrupo: number }) {
  if (l.cachosGrupo <= 0) return "0%";
  return `${Math.round((l.cachosAlocados / l.cachosGrupo) * 100)}%`;
}

/** Agrupa as associações (dia,setor) por dia para exibição — ex.: "01/07 (A2, B3) · 02/07 (B1)". */
function textoColheitaAssociada(associacoes: Array<{ data: string; setor: { nome: string } }>): string {
  const porDia = new Map<string, string[]>();
  for (const a of associacoes) {
    if (!porDia.has(a.data)) porDia.set(a.data, []);
    porDia.get(a.data)!.push(a.setor.nome);
  }
  return Array.from(porDia.entries())
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([data, setores]) => `${fmtData(data)} (${setores.join(", ")})`)
    .join(" · ");
}

function romaneioFilename(venda: { numero: number | null; ano: number; data: string }, extensao: "pdf" | "png" = "pdf"): string {
  const numeroAno = venda.numero != null ? `${String(venda.numero).padStart(3, "0")}-${venda.ano}` : `rascunho-${venda.ano}`;
  return `Romaneio_${numeroAno}_${venda.data}.${extensao}`;
}

/** Legenda da mensagem no compartilhamento mobile. O WhatsApp RENOMEIA o arquivo ao
 *  transmitir (IMG-AAAAMMDD-WA…) — foi isso que fez o romaneio "aparecer com a data de hoje";
 *  o app sempre imprimiu a data da venda. Com o número e a data na legenda, eles ficam na
 *  conversa, onde nada os renomeia. */
function legendaCompartilhamento(venda: { numero: number | null; ano: number; data: string }): string {
  const numeroAno = venda.numero != null ? `${String(venda.numero).padStart(3, "0")}/${venda.ano}` : `rascunho/${venda.ano}`;
  return `Romaneio ${numeroAno} — ${fmtData(venda.data)}`;
}

/** Web Share API só faz sentido em mobile/tablet; no desktop queremos download/clipboard.
 *  iPadOS se identifica como "Mac", por isso o teste de maxTouchPoints. */
function isMobileShareTarget(): boolean {
  return (
    /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
    (navigator.maxTouchPoints > 2 && /Mac/i.test(navigator.userAgent))
  );
}

/** % de cada item relativa ao total da MESMA variedade (não da carga inteira) — mesma
 * lógica de server/lib/romaneio-layout.ts, replicada aqui porque a tela de detalhe
 * não passa pelo PDF/PNG. Retorna "" quando nenhum grupo de variedade tem mais de 1 item. */
function textoDistribuicao(itens: Array<{ pesoTotalKg: string; produtoVenda: { nome: string; variedadeId: string | null } }>): string {
  const porVariedade = new Map<string, typeof itens>();
  for (const item of itens) {
    const chave = item.produtoVenda.variedadeId ?? "__sem_variedade__";
    if (!porVariedade.has(chave)) porVariedade.set(chave, []);
    porVariedade.get(chave)!.push(item);
  }

  // Só grupos com mais de 1 item qualificam; varre `itens` de novo (em vez de concatenar
  // grupo a grupo) pra manter a ordem original mesmo se a mesma variedade não estiver
  // contígua na lista.
  const totalPorGrupoQualificado = new Map<string, number>();
  for (const [chave, itensGrupo] of Array.from(porVariedade.entries())) {
    if (itensGrupo.length <= 1) continue;
    const totalGrupo = itensGrupo.reduce((acc, it) => acc + parseFloat(it.pesoTotalKg), 0);
    if (totalGrupo <= 0) continue;
    totalPorGrupoQualificado.set(chave, totalGrupo);
  }

  const partes: string[] = [];
  for (const item of itens) {
    const chave = item.produtoVenda.variedadeId ?? "__sem_variedade__";
    const totalGrupo = totalPorGrupoQualificado.get(chave);
    if (totalGrupo === undefined) continue;
    const pct = (parseFloat(item.pesoTotalKg) / totalGrupo) * 100;
    partes.push(`${item.produtoVenda.nome.replace(/^Banana\s+/i, "")} ${pct.toFixed(1).replace(".", ",")}%`);
  }
  return partes.length > 0 ? `Distribuição: ${partes.join(" · ")}` : "";
}

/** Número em tabela é sempre `font-mono tabular-nums text-right` — a regra do
 *  `DataTable`, aplicada à mão aqui porque o romaneio não usa o componente. */
const NUM = "px-3 py-2 text-right font-mono tabular-nums";

/** ISO de um instante que chega do JSON como string (ou como Date, nos testes). */
function isoDe(v: unknown): string | null {
  if (!v) return null;
  const d = new Date(v as string);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export default function VendaDetalhePage() {
  const { id } = useParams<{ id: string }>();
  // "Voltar" é voltar de verdade: devolve à lista de ONDE se veio (com filtros e
  // rolagem), e não a `/cacho/vendas` fixo — a mesma venda também se abre de
  // `/mercado/receber`, e mandar a pessoa para outra tela é perder o lugar duas vezes.
  const { voltar, abrir, encerrar } = useNavegacao();
  const { toast } = useToast();
  // PR 2: despagar/excluir é `cacho.vendas.financeiro`.
  const isAdmin = usePode("cacho.vendas.financeiro");

  const { data: venda, isLoading } = useVenda(id);
  const { data: rateio } = useRateioVenda(venda?.status !== "CANCELADA" ? id : undefined);
  // Formalizada? Então editar o vencimento muda o que o comprador lê no link, e o
  // seletor avisa isso antes — o link renderiza o dado vivo.
  const { data: compartilhamentos } = useCompartilhamentoDaVenda(id);
  const formalizada = (compartilhamentos ?? []).some(c => !c.revogadoEm);
  // O pagamento que o COMPRADOR registrou no Mercado — o que deixou esta venda
  // PAGA sem ninguém da fazenda clicar. Sem esta linha, a venda aparecia paga e
  // o produtor não tinha como saber quando nem por quem.
  const compartilhamentoVivo = (compartilhamentos ?? []).find(c => !c.revogadoEm);
  const pagoPeloComprador = compartilhamentoVivo?.pagamentoDoComprador ?? null;
  // A venda nasceu de um registro do comprador? Troca o "Emitir" por "Emitir e
  // confirmar com {comprador}" — e, depois, tira o painel de envio.
  const { data: origem } = useOrigemDaVenda(id, !!venda && venda.status !== "CANCELADA");
  const emitirEConfirmar = useEmitirEConfirmar();
  const podeReceber = usePode("mercado.receber");
  const podeFormalizar = usePode("mercado.operar");
  // O compartilhamento que VALE para a linha do tempo: o vivo; o revogado só
  // quando não há outro (a etapa mostra "revogada").
  const compartilhamentoDaLinha = compartilhamentoVivo ?? (compartilhamentos ?? [])[0];
  // SEM conta no Rizoma, o link é o único canal: o WhatsApp da barra manda o
  // link (e marca enviado). Com conta, o romaneio já está na tela dele.
  const envioPorLink = !!compartilhamentoVivo && !compartilhamentoVivo.compradorNoRizoma;
  const { data: mensagemLink } = useMensagemCompartilhamento(envioPorLink ? compartilhamentoVivo!.id : undefined);
  const marcarEnviado = useMarcarEnviado();
  const desfazerEnvio = useDesfazerEnvio();
  // `?secao=conferencia` — veio do chip "Divergência" da lista.
  const focarConferencia = new URLSearchParams(useSearch()).get("secao") === "conferencia";
  const emitir = useEmitirVenda();
  // `usePagarVenda` não fica aqui: quem confirma o recebimento é o DialogRecebi,
  // que traz junto a data — ver o bloco do diálogo, no fim do arquivo.
  const despagar = useDespagarVenda();
  const cancelar = useCancelarVenda();
  const excluir = useExcluirVenda();

  const [confirmarPagoOpen, setConfirmarPagoOpen] = useState(false);
  const [naoRecebiOpen, setNaoRecebiOpen] = useState(false);
  const [emitirConfirmarOpen, setEmitirConfirmarOpen] = useState(false);
  const [erroEmissao, setErroEmissao] = useState<string | null>(null);
  const [formalizarOpen, setFormalizarOpen] = useState(false);
  const [gerandoPdf, setGerandoPdf] = useState(false);
  const [gerandoImagem, setGerandoImagem] = useState(false);
  const [gerandoWhatsapp, setGerandoWhatsapp] = useState(false);

  if (isLoading || !venda) {
    return <div className="p-4 md:p-6"><PageSkeleton variante="cards" linhas={4} /></div>;
  }

  const editavel = podeEditarVenda(venda);
  // Derivado dos ITENS — não existe campo de modo em `vendas`, e não deve haver: dois
  // lugares guardando a mesma verdade divergem. O predicado mora em shared/ e é o MESMO
  // que o romaneio usa (o servidor e a tela não podem discordar do que é "carga pesada").
  const emPesagem = cargaEmPesagem(venda.itens);
  // O selo do topo é o MESMO chip de dinheiro da lista (A RECEBER · PARCIAL ·
  // CONTESTADO · PAGA) — "Emitida" aqui e "A RECEBER" na linha eram dois nomes.
  const dinheiro = estadoDinheiroDaVenda({
    statusVenda: venda.status,
    statusPagamento: compartilhamentoVivo ? compartilhamentoVivo.statusPagamento : null,
  });
  const etapas = linhaDoTempoDaVenda({
    status: venda.status,
    criadaEm: isoDe(venda.createdAt) ?? new Date().toISOString(),
    emitidaEm: isoDe(venda.emitidaEm),
    pagaEm: isoDe(venda.pagaEm),
    vencimento: venda.vencimento ?? null,
    vencimentoDefinidoPor: venda.vencimentoDefinidoPor ?? null,
    compartilhamento: compartilhamentoDaLinha,
    compradorNome: venda.comprador.nome,
    hoje: hojeNaFazenda(),
  });
  const mostraFormalizar = (venda.status === "EMITIDA" || venda.status === "PAGA") && !compartilhamentoVivo && podeFormalizar;
  // O que MARCAR COMO PAGA quita: base (a conferência do comprador manda sobre
  // o emitido) − o que já entrou. Vem do servidor — o mesmo número que ele
  // lança ao quitar. Sem formalização não há conferência: vale o emitido.
  const aReceber = compartilhamentoVivo ? compartilhamentoVivo.aReceber : parseFloat(venda.valorTotal);

  // Itens do menu "⋯" — mesmo gating de permissão já usado nos botões atuais.
  const podeEditarNoMenu = venda.status === "EMITIDA";
  // Pago pelo COMPRADOR não se desfaz daqui (o servidor recusa com 409): o
  // caminho de volta é o "Não recebi", que avisa o Mercado junto.
  const podeDespagarNoMenu = venda.status === "PAGA" && isAdmin && !pagoPeloComprador;
  // "Não recebi" — escondido no ⋯ de propósito: quem registra quita, e isto é a
  // reclamação de quem não viu o dinheiro cair, não um segundo ato esperado.
  const podeNaoRecebiNoMenu = !!pagoPeloComprador && podeReceber && (venda.status === "PAGA" || venda.status === "EMITIDA");
  const podeCancelarNoMenu = venda.status === "RASCUNHO" || venda.status === "EMITIDA";
  const podeExcluirNoMenu = isAdmin && (venda.status === "RASCUNHO" || venda.status === "CANCELADA");
  const podeDesfazerEnvioNoMenu = envioPorLink && !!compartilhamentoVivo?.enviadoEm;
  const temItensNoMenu = podeEditarNoMenu || podeDespagarNoMenu || podeNaoRecebiNoMenu || podeCancelarNoMenu
    || podeExcluirNoMenu || envioPorLink;
  // A venda que o comprador registrou, com ele no Rizoma: emitir já formaliza.
  const emitirConfirmando = venda.status === "RASCUNHO" && !!origem?.adotada && !!origem?.compradorNoRizoma;
  const contestacao = compartilhamentoVivo?.contestacao ?? null;

  async function handleEmitir() {
    try {
      await emitir.mutateAsync(venda!.id);
      toast({ title: "Venda emitida!" });
    } catch (e: any) {
      toast({ title: "Erro ao emitir", description: e.message, variant: "destructive" });
    }
  }
  /** Com prazo (o que o comprador combinou), emite direto; sem prazo, o diálogo pede um. */
  function handleEmitirEConfirmar(vencimento: string | null) {
    setErroEmissao(null);
    emitirEConfirmar.mutate(
      { vendaId: venda!.id, vencimento },
      {
        onSuccess: r => {
          setEmitirConfirmarOpen(false);
          toast({
            title: `Romaneio ${r.numero} emitido`,
            description: `Formalizado com ${venda!.comprador.nome} — já está na tela dele no Rizoma.`,
          });
        },
        // Nunca engolir: 409/422 chegam com o motivo, no diálogo ou no toast.
        onError: (e: Error) => {
          const mensagem = mensagemDeErroDaEmissao(e);
          if (vencimento) setErroEmissao(mensagem);
          else toast({ title: "Não deu para emitir", description: mensagem, variant: "destructive" });
        },
      },
    );
  }
  async function handleCopiarTextoDoLink() {
    if (!mensagemLink) return;
    try {
      await navigator.clipboard.writeText(mensagemLink.texto);
      toast({ title: "Texto copiado!", description: `Cole na conversa com ${venda!.comprador.nome}.` });
    } catch (e: any) {
      toast({ title: "Não consegui copiar", description: e.message, variant: "destructive" });
    }
  }
  async function handleDesfazerEnvio() {
    try {
      await desfazerEnvio.mutateAsync(compartilhamentoVivo!.id);
      toast({ title: "Marcação desfeita." });
    } catch (e: any) {
      toast({ title: "Erro ao desfazer", description: e.message, variant: "destructive" });
    }
  }
  async function handleDespagar() {
    try {
      await despagar.mutateAsync(venda!.id);
      toast({ title: "Pagamento desfeito." });
    } catch (e: any) {
      toast({ title: "Erro ao desfazer pagamento", description: e.message, variant: "destructive" });
    }
  }
  async function handleCancelar() {
    if (!confirm("Cancelar esta venda? Esta ação não pode ser desfeita.")) return;
    try {
      await cancelar.mutateAsync(venda!.id);
      toast({ title: "Venda cancelada." });
    } catch (e: any) {
      toast({ title: "Erro ao cancelar", description: e.message, variant: "destructive" });
    }
  }
  async function handleExcluir() {
    if (!confirm("Excluir esta venda definitivamente? Os itens e a colheita associada também serão removidos. Esta ação NÃO pode ser desfeita.")) return;
    try {
      await excluir.mutateAsync(venda!.id);
      toast({ title: "Venda excluída." });
      // `encerrar`, não `voltar`: com `replace` a entrada do detalhe morto sai do
      // histórico e um "voltar" seguinte não reabre um 404.
      encerrar("/cacho/vendas");
    } catch (e: any) {
      toast({ title: "Erro ao excluir", description: e.message, variant: "destructive" });
    }
  }

  async function handleRomaneioPdf() {
    if (!venda) return;
    setGerandoPdf(true);
    try {
      const res = await fetch(`/api/vendas/${venda.id}/romaneio.pdf`, { credentials: "include" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? "Erro ao gerar o Romaneio PDF");
      }
      const blob = await res.blob();
      const filename = romaneioFilename(venda);
      const file = new File([blob], filename, { type: "application/pdf" });

      if (isMobileShareTarget() && navigator.canShare?.({ files: [file] })) {
        // `text` vira a LEGENDA da mensagem no celular. O WhatsApp renomeia o arquivo ao
        // transmitir (IMG-AAAAMMDD-WA…), então o número e a data da venda só sobrevivem se
        // forem para a conversa — não para o nome do arquivo.
        await navigator.share({ files: [file], title: filename, text: legendaCompartilhamento(venda) });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (e: any) {
      if (e?.name !== "AbortError") {
        toast({ title: "Erro ao gerar o Romaneio PDF", description: e.message, variant: "destructive" });
      }
    } finally {
      setGerandoPdf(false);
    }
  }

  async function handleRomaneioImagem() {
    if (!venda) return;
    setGerandoImagem(true);
    try {
      const res = await fetch(`/api/vendas/${venda.id}/romaneio.png`, { credentials: "include" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? "Erro ao gerar a imagem do Romaneio");
      }
      const blob = await res.blob();
      const filename = romaneioFilename(venda, "png");
      const file = new File([blob], filename, { type: "image/png" });

      if (isMobileShareTarget() && navigator.canShare?.({ files: [file] })) {
        // `text` vira a LEGENDA da mensagem no celular. O WhatsApp renomeia o arquivo ao
        // transmitir (IMG-AAAAMMDD-WA…), então o número e a data da venda só sobrevivem se
        // forem para a conversa — não para o nome do arquivo.
        await navigator.share({ files: [file], title: filename, text: legendaCompartilhamento(venda) });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (e: any) {
      if (e?.name !== "AbortError") {
        toast({ title: "Erro ao gerar a imagem do Romaneio", description: e.message, variant: "destructive" });
      }
    } finally {
      setGerandoImagem(false);
    }
  }

  async function handleWhatsapp() {
    if (!venda) return;
    setGerandoWhatsapp(true);
    try {
      const { texto } = await fetchJson<{ texto: string }>(`/api/vendas/${venda.id}/whatsapp`);
      if (isMobileShareTarget() && navigator.share) {
        await navigator.share({ text: texto });
      } else {
        await navigator.clipboard.writeText(texto);
        toast({ title: "Texto copiado!", description: "Cole no WhatsApp." });
      }
    } catch (e: any) {
      if (e?.name !== "AbortError") {
        toast({ title: "Erro ao gerar o texto do WhatsApp", description: e.message, variant: "destructive" });
      }
    } finally {
      setGerandoWhatsapp(false);
    }
  }

  return (
    /* Coluna única COM TETO (1024px), não faixa estreita nem largura livre.
       `max-w-4xl` virava um miolo de 900px no meio de 1900; esticar até a borda
       e abrir uma coluna lateral quebrou pior — a lateral não cabia os três
       botões de compartilhar e o WhatsApp saía da tela. O teto resolve os dois:
       em 1440 sobra pouca margem, em 1900 centraliza. Se 1024 ficar estreito no
       monitor grande, o ajuste é trocar por `max-w-6xl` — não voltar a esticar. */
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4 pb-24">
      <Button variant="ghost" onClick={() => voltar("/cacho/vendas")} className="w-fit -ml-2" data-testid="button-back">
        <ArrowLeft className="h-4 w-4 mr-2" />
        Voltar
      </Button>

      <PageHeader
        titulo={venda.numero ? `Venda nº ${String(venda.numero).padStart(3, "0")}/${venda.ano}` : "Venda (rascunho)"}
        acao={
          temItensNoMenu ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" aria-label="Mais ações">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {podeEditarNoMenu && (
                  <DropdownMenuItem onClick={() => abrir(`/cacho/vendas/${venda.id}/editar`)}>
                    <Pencil className="h-4 w-4 mr-2" />Editar venda
                  </DropdownMenuItem>
                )}
                {podeDespagarNoMenu && (
                  <DropdownMenuItem onClick={handleDespagar} disabled={despagar.isPending}>
                    <RotateCcw className="h-4 w-4 mr-2" />Desfazer pagamento
                  </DropdownMenuItem>
                )}
                {envioPorLink && (
                  <DropdownMenuItem onClick={handleCopiarTextoDoLink} disabled={!mensagemLink} data-testid="menu-copiar-texto">
                    <Copy className="h-4 w-4 mr-2" />Copiar texto com o link
                  </DropdownMenuItem>
                )}
                {podeDesfazerEnvioNoMenu && (
                  <DropdownMenuItem onClick={handleDesfazerEnvio} disabled={desfazerEnvio.isPending} data-testid="menu-nao-enviei">
                    <Undo2 className="h-4 w-4 mr-2" />Não enviei o link
                  </DropdownMenuItem>
                )}
                {podeNaoRecebiNoMenu && (
                  <DropdownMenuItem onClick={() => setNaoRecebiOpen(true)} data-testid="menu-nao-recebi">
                    <CircleAlert className="h-4 w-4 mr-2" />Não recebi este pagamento
                  </DropdownMenuItem>
                )}
                {podeEditarNoMenu && podeCancelarNoMenu && <DropdownMenuSeparator />}
                {podeCancelarNoMenu && (
                  <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={handleCancelar}>
                    <Ban className="h-4 w-4 mr-2" />Cancelar venda
                  </DropdownMenuItem>
                )}
                {podeExcluirNoMenu && (
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onClick={handleExcluir}
                    disabled={excluir.isPending}
                    data-testid="button-excluir"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />Excluir venda
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : undefined
        }
      />

      <div className="flex items-center gap-2 flex-wrap">
        <ChipDinheiro estado={dinheiro} testIdSufixo="venda" />
        <span className="text-sm text-muted-foreground">
          <b className="text-foreground font-semibold">{fmtData(venda.data)}</b>
          {" · "}
          {/* O nome como está no cadastro — a caixa alta é do DOCUMENTO (o romaneio
              de carga, embaixo), não da linha de resumo. */}
          <b className="text-foreground font-semibold">{venda.comprador.nome}</b>
          {/* Segmento OMITIDO em pesagem — não "0 cx", que afirmaria contagem zero,
              nem "—", que no app já significa "não sei". */}
          {!emPesagem && <>{" · "}{venda.totalCaixas.toLocaleString("pt-BR")} cx</>}
          {" · "}{fmtKg(venda.pesoTotalKg)}
        </span>
      </div>

      {/* BARRA DE SITUAÇÃO E AÇÕES — a linha do tempo em cima, as ações embaixo.
          É o lugar de TUDO o que diz "em que pé está": emitida, formalizada (e
          onde — na tela do comprador ou pelo link), conferida, paga (e por
          quem, no tooltip). Os três blocos que repetiam isso embaixo — o card "Na
          tela de Sávio" com "Enviado ▸ 15:10", a faixa verde do pagamento e a
          tabela da conferência que bate — viraram a bolinha de cada etapa. */}
      <div className="rounded-xl border bg-card shadow-xs px-3 py-3 md:px-4 flex flex-col gap-3">
        {venda.status !== "CANCELADA" && (
          <LinhaDoTempoVenda
            etapas={etapas}
            onAbrirConferencia={() => document.getElementById("conferencia")?.scrollIntoView({ behavior: "smooth", block: "start" })}
          />
        )}

        {/* Em md+ os botões têm LARGURA NATURAL e ficam numa linha: `md:min-w-[16rem]`
            no principal e `w-full` nos outros era o que estourava a barra. No celular
            a ordem é a de hoje — principal primeiro, os três de compartilhar num
            `grid-cols-3` embaixo. */}
        <div className="flex flex-col gap-2 md:flex-row md:flex-wrap md:items-center md:justify-end md:border-t md:pt-3">
          {venda.status === "RASCUNHO" && (emitirConfirmando ? (
            // A venda que o COMPRADOR registrou: emitir já formaliza com ele, e o
            // botão diz isso (regra 1 — o compartilhamento nasce de ato explícito).
            <div className="flex flex-col gap-1 md:order-3 md:items-end">
              <Button
                className="h-11 w-full md:w-auto"
                onClick={() => (venda.vencimento ? handleEmitirEConfirmar(null) : setEmitirConfirmarOpen(true))}
                disabled={emitirEConfirmar.isPending}
                data-testid="button-emitir-e-confirmar"
              >
                <Handshake className="h-4 w-4 mr-2" />
                {emitirEConfirmar.isPending ? "Emitindo…" : `Emitir e confirmar com ${venda.comprador.nome}`}
              </Button>
              <p className="text-xs text-muted-foreground md:text-right" data-testid="texto-emitir-e-confirmar">
                {venda.comprador.nome} registrou esta compra. Ao emitir, o romaneio fica formalizado com ele.
              </p>
            </div>
          ) : (
            <Button className="h-11 w-full md:w-auto md:order-3" onClick={handleEmitir} disabled={emitir.isPending}>
              <Send className="h-4 w-4 mr-2" />Emitir venda
            </Button>
          ))}
          {venda.status === "EMITIDA" && (
            <Button className="h-11 w-full md:w-auto md:order-3" onClick={() => setConfirmarPagoOpen(true)}>
              <Check className="h-4 w-4 mr-2" />Marcar como paga — {fmtMoney(aReceber)}
            </Button>
          )}

          {/* Formalizar é a etapa que falta na linha do tempo — o botão mora
              junto das outras ações, não num card próprio embaixo. No celular vem logo
              depois da ação principal: é a próxima etapa. */}
          {mostraFormalizar && (
            <Button variant="outline" className="h-11 w-full md:w-auto md:order-2" onClick={() => setFormalizarOpen(true)} data-testid="button-formalizar">
              <Handshake className="h-4 w-4 mr-2" />Formalizar com {venda.comprador.nome}
            </Button>
          )}
          {venda.status === "RASCUNHO" && (
            <Button variant="outline" className="h-11 w-full md:w-auto md:order-1" onClick={() => abrir(`/cacho/vendas/${venda.id}/editar`)}>
              <Pencil className="h-4 w-4 mr-2" />Editar
            </Button>
          )}

          {(venda.status === "EMITIDA" || venda.status === "PAGA") && (
            <>
              {/* Rótulos CURTOS (PDF, não "Romaneio PDF"): é o que faz os três
                  caberem na linha junto da ação principal. */}
              <div className="grid grid-cols-3 gap-2 md:flex md:items-center md:order-1">
                <Button variant="outline" className="h-11" onClick={handleRomaneioPdf} disabled={gerandoPdf} data-testid="button-romaneio-pdf">
                  <FileText className="h-4 w-4 mr-2" />{gerandoPdf ? "Gerando…" : "PDF"}
                </Button>
                <Button variant="outline" className="h-11" onClick={handleRomaneioImagem} disabled={gerandoImagem} data-testid="button-romaneio-imagem">
                  <Image className="h-4 w-4 mr-2" />{gerandoImagem ? "Gerando…" : "Imagem"}
                </Button>
                {envioPorLink ? (
                  // Comprador SEM conta: o WhatsApp manda o LINK (o único canal
                  // dele) e marca enviado no mesmo toque — o que o card "Enviar
                  // para o comprador" fazia, agora na barra.
                  <Button variant="outline" className="h-11" asChild={!!mensagemLink} disabled={!mensagemLink} data-testid="button-whatsapp">
                    {mensagemLink ? (
                      <a
                        href={mensagemLink.urlWhatsApp}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => {
                          if (compartilhamentoVivo && !compartilhamentoVivo.enviadoEm) marcarEnviado.mutate(compartilhamentoVivo.id);
                        }}
                      >
                        <MessageCircle className="h-4 w-4 mr-2" />WhatsApp
                      </a>
                    ) : (
                      <><MessageCircle className="h-4 w-4 mr-2" />WhatsApp</>
                    )}
                  </Button>
                ) : (
                  <Button variant="outline" className="h-11" onClick={handleWhatsapp} disabled={gerandoWhatsapp} data-testid="button-whatsapp">
                    <MessageCircle className="h-4 w-4 mr-2" />{gerandoWhatsapp ? "Gerando…" : "WhatsApp"}
                  </Button>
                )}
              </div>
              {/* Separa "compartilhar a nota" de "mudar o estado da venda". */}
              <div className="hidden md:block w-px h-7 bg-border mx-1 md:order-2" />
            </>
          )}
        </div>
      </div>

      {/* O "Não recebi" que ainda vale: o romaneio voltou a ficar em aberto até o
          comprador registrar de novo (e aí quita, como sempre). */}
      {contestacao && (
        <div
          className="rounded-xl border border-erro/30 bg-erro-suave px-4 py-2.5 text-sm text-erro flex items-start gap-2"
          data-testid="pagamento-contestado"
        >
          <CircleAlert className="h-4 w-4 shrink-0 mt-0.5" />
          <span>
            Você contestou o pagamento de <b>{venda.comprador.nome}</b> ({fmtMoney(contestacao.valor)}) em {fmtDataHora(contestacao.em)}
            {contestacao.motivo ? <>: “{contestacao.motivo}”</> : null}. O romaneio está em aberto até {venda.comprador.nome} registrar de novo.
          </span>
        </div>
      )}

      {/* A conferência lado a lado SÓ quando não bate — é para cá que o chip
          "Divergência" da lista e o "−120 kg" da linha do tempo trazem. Quando
          bate, a bolinha verde de "Conferida" diz tudo: duas colunas iguais não
          acrescentam nada. */}
      {compartilhamentoVivo?.divergencia && (
        <ConferenciaDaVenda
          compartilhamentoId={compartilhamentoVivo.id}
          compradorNome={venda.comprador.nome}
          focar={focarConferencia}
          vendaId={venda.id}
          vendaNumero={venda.numero ? `${String(venda.numero).padStart(3, "0")}/${venda.ano}` : "rascunho"}
          vendaStatus={venda.status}
        />
      )}

      <div className="rounded-lg border bg-card shadow-xs overflow-hidden">
        {/* A faixa preta é a IDENTIDADE do romaneio — é o documento que o
            comprador recebe, e ele tem que se parecer com um documento mesmo
            dentro do app. O que muda aqui é só o miolo: tipografia de número
            do sistema, e a meta em três colunas quando há largura. */}
        <div className="bg-foreground text-background px-4 py-2.5 flex items-center justify-between gap-3">
          <p className="font-extrabold text-sm tracking-wide">ROMANEIO DE CARGA</p>
          <span className="text-[11px] font-semibold opacity-70 tabular-nums">
            {venda.numero ? `Nº ${String(venda.numero).padStart(3, "0")}/${venda.ano}` : "rascunho"}
          </span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 p-4 border-b text-xs">
          <div><span className="text-muted-foreground">Data:</span> <b>{fmtData(venda.data)}</b></div>
          {/* Ao lado da Data, porque é a outra data do romaneio — e é o mesmo
              seletor da linha de "A receber": um lugar para a regra, dois para
              usá-la. Sem data, "a definir"; com data, a data e um lápis. */}
          <div className="flex items-center gap-1">
            <span className="text-muted-foreground">Vencimento:</span>
            <b>
              <SeletorVencimento
                id={venda.id}
                vencimento={venda.vencimento ?? null}
                dataVenda={venda.data}
                formalizado={formalizada}
                testIdSufixo="venda"
              />
            </b>
          </div>
          <div><span className="text-muted-foreground">Comprador:</span> <b>{venda.comprador.nome.toUpperCase()}</b></div>
        </div>
        {/* Sem `colgroup`/`table-fixed` de propósito: numa tabela de UMA venda o
            layout automático cabe no celular sem rolagem lateral. O que se alinha
            com o resto do app é a TIPOGRAFIA — cabeçalho em caixa alta com
            tracking, número em `font-mono tabular-nums` (a regra do DataTable). */}
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-muted/40 text-muted-foreground uppercase text-[10px] font-semibold tracking-wider">
              <th className="text-left px-3 py-2">Produto</th>
              {/* Numa tabela de UMA venda, `cargaEmPesagem` garante que ou todas as linhas são
                  por peso ou nenhuma é — então a coluna inteira sai, em vez de repetir um
                  marcador linha a linha. Coluna vazia de ponta a ponta não é informação: é um
                  lugar onde o leitor procura número e não acha.
                  (Na LISTA de vendas o caso é outro e a saída é outra: lá o recorte pode ser
                  misto, a coluna precisa existir para as outras linhas, e a célula diz
                  "por peso".) */}
              {!emPesagem && <th className="text-right px-3 py-2">Cx</th>}
              {!emPesagem && <th className="text-right px-3 py-2">Kg/cx</th>}
              <th className="text-right px-3 py-2">Total kg</th>
              <th className="text-right px-3 py-2">R$/kg</th>
              <th className="text-right px-3 py-2">Valor</th>
            </tr>
          </thead>
          <tbody>
            {venda.itens.map(it => (
              <tr key={it.id} className="border-t">
                <td className="px-3 py-2 font-medium">{it.produtoVenda.nome}</td>
                {!emPesagem && <td className={NUM}>{it.quantidadeCaixas.toLocaleString("pt-BR")}</td>}
                {!emPesagem && <td className={NUM}>{fmtDecimal(it.pesoCaixaKg ?? 0, 0)}</td>}
                <td className={NUM}>{fmtKg(it.pesoTotalKg)}</td>
                {/* "1,40", não "1.40": `toFixed` é ponto americano (print do Hugo, 24/09). */}
                <td className={NUM}>{fmtDecimal(it.precoKg, 2)}</td>
                <td className={cn(NUM, "font-bold text-foreground")}>{fmtMoney(it.valorTotal)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex justify-between items-center bg-foreground text-background px-4 py-3">
          <span className="text-xs font-bold tracking-wide">VALOR TOTAL DA CARGA</span>
          <span className="text-lg font-extrabold text-atencao-claro tabular-nums">{fmtMoney(venda.valorTotal)}</span>
        </div>
        <div className="px-4 py-2 text-[11px] italic text-muted-foreground border-b">
          {emPesagem
            ? `Quantidade total de kg: ${fmtKg(venda.pesoTotalKg)}`
            : `Total geral: ${venda.totalCaixas.toLocaleString("pt-BR")} caixas · ${fmtKg(venda.pesoTotalKg)}`}
        </div>
        {textoDistribuicao(venda.itens) && (
          <div className="px-4 py-2 text-[11px] italic text-muted-foreground border-b">
            {textoDistribuicao(venda.itens)}
          </div>
        )}
      </div>

      {venda.colheitaAssociacoes.length > 0 && (
        <div className="rounded-lg border bg-card shadow-xs p-4" data-testid="card-colheita-associada">
          <div className="flex justify-between items-baseline mb-1">
            <h3 className="font-bold text-sm">Colheita associada</h3>
            <button
              className={editavel ? "text-xs text-primary font-semibold" : "text-xs text-muted-foreground font-semibold cursor-not-allowed"}
              disabled={!editavel}
              title={!editavel ? `Venda ${venda.status === "PAGA" ? "paga" : "cancelada"} — não é possível editar a colheita associada` : undefined}
              onClick={() => editavel && abrir(`/cacho/vendas/${venda.id}/editar`)}
              data-testid="button-editar-colheita-associada"
            >
              Editar
            </button>
          </div>
          <p className="text-xs text-muted-foreground">{textoColheitaAssociada(venda.colheitaAssociacoes)}</p>
        </div>
      )}

      {/* O que mudou depois de formalizada — vencimento e correções, com quem e
          quando. Sem formalização não há evento do Mercado. */}
      {compartilhamentoVivo && <HistoricoDaVenda compartilhamentoId={compartilhamentoVivo.id} />}

      {venda.status !== "CANCELADA" && rateio && rateio.linhas.length > 0 && (
        <div className="rounded-lg border bg-card shadow-xs p-4" data-testid="card-rateio">
          <div className="flex justify-between items-baseline mb-1">
            <h3 className="font-bold text-sm">Rateio por setor</h3>
            <span className="text-[10px] bg-muted rounded-full px-2 py-0.5 font-bold text-muted-foreground">
              {rateio.previa ? "PRÉVIA" : "ESTIMADO"}
            </span>
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            Colheita {fmtData(rateio.periodo.inicio)}–{fmtData(rateio.periodo.fim)} · alocação proporcional ao kg de cada venda
            {rateio.previa && " · rascunho não consome saldo: isto é o que aconteceria na emissão"}
            {/* Timestamp velho é o sinal mais barato que existe de número velho: se o
                recálculo de um gatilho falhou, ele fica para trás e aparece aqui. */}
            {rateio.calculadoEm && (
              <span className="block text-[10px] mt-0.5" data-testid="texto-rateio-calculado-em">
                rateio calculado em {fmtDataHora(rateio.calculadoEm)}
              </span>
            )}
          </p>

          {rateio.pesoMedioPorVariedade.map((v, i) => (
            <div key={i} className="mt-3">
              <div className="flex justify-between text-xs font-bold border-b pb-1 gap-2">
                <span>{v.variedadeNome ?? "Sem variedade"} — {fmtKg(v.pesoTotalKg)} · {fmtMoney(v.valorTotal)}</span>
                <span className={v.avisoPesoImplausivel ? "text-atencao font-extrabold shrink-0" : "text-primary font-extrabold shrink-0"} data-testid="texto-peso-medio">
                  {v.pesoMedioKg !== null ? `${fmtKgPorCacho(v.pesoMedioKg)} kg/cacho · ${fmtCachos(v.cachos)} cachos` : "—"}
                </span>
              </div>
              {v.avisoPesoImplausivel && (
                <p className="text-[11px] text-atencao mt-1" data-testid="aviso-peso-implausivel">
                  ⚠️ {fmtKg(v.pesoTotalKg)} ÷ {fmtCachos(v.cachos)} cachos = {v.pesoMedioKg !== null ? fmtKgPorCacho(v.pesoMedioKg) : "—"} kg/cacho — fora
                  da faixa esperada ({fmtKgPorCacho(v.faixaAviso?.min ?? 5)}–{fmtKgPorCacho(v.faixaAviso?.max ?? 40)} kg/cacho
                  {/* A régua tem que se identificar: uma faixa APRENDIDA da variedade e um
                      chute de 5–40 não merecem a mesma confiança, e a tela não pode
                      apresentar os dois do mesmo jeito. */}
                  {v.faixaAviso?.aprendida
                    ? `, aprendida das colheitas de ${v.variedadeNome ?? "a variedade"}`
                    : ", faixa padrão — ainda não há histórico suficiente desta variedade"}
                  ). A colheita associada provavelmente está incompleta ou aponta pros setores errados.
                  {editavel && (
                    <>
                      {" "}
                      <button
                        type="button"
                        className="underline font-semibold"
                        onClick={() => abrir(`/cacho/vendas/${venda.id}/editar`)}
                        data-testid="link-revisar-colheita-associada"
                      >
                        Revisar colheita associada
                      </button>
                    </>
                  )}
                </p>
              )}
            </div>
          ))}

          {/* Uma linha por (dia, setor) reivindicado — inclusive as INÓCUAS, que é o que
              denuncia associação marcada no setor errado. O modelo antigo as aproveitava
              em silêncio, espalhando kg pelos setores de outra variedade. */}
          <div className="mt-3 space-y-0.5" data-testid="lista-rateio-linhas">
            {rateio.linhas.map(l => (
              <div key={`${l.data}|${l.setorId}`} className="py-1.5 border-b border-dashed text-xs" data-testid="linha-rateio">
                <div className="flex items-center gap-2">
                  <span className="font-bold w-10 shrink-0">{l.setorNome}</span>
                  <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                    <div
                      className={l.inocua ? "h-full bg-atencao rounded-full" : "h-full bg-primary rounded-full"}
                      style={{ width: `${l.cachosGrupo > 0 ? Math.min(100, (l.cachosAlocados / l.cachosGrupo) * 100) : 0}%` }}
                    />
                  </div>
                  <span className="w-20 text-right text-muted-foreground shrink-0">{fmtKg(l.kgAlocados)}</span>
                </div>
                <div className="flex items-baseline justify-between gap-2 mt-0.5 pl-12">
                  <span className="text-[11px] text-muted-foreground">
                    {fmtData(l.data)} · {fmtCachos(l.cachosAlocados)} de {l.cachosGrupo} cachos ({pctAlocado(l)})
                  </span>
                  {l.outrasVendas > 0 && (
                    <span className="text-[10px] text-muted-foreground font-semibold shrink-0" data-testid="rotulo-compartilhado">
                      {/* "compartilhado com 1 venda" lido literalmente dá o número errado:
                          `outrasVendas` conta as OUTRAS, então o grupo está dividido entre 2.
                          "mais N" torna o rótulo verdadeiro sem mudar o número. */}
                      dividido com mais {l.outrasVendas} venda{l.outrasVendas > 1 ? "s" : ""}
                    </span>
                  )}
                </div>
                {l.inocua && (
                  <p className="text-[11px] text-atencao mt-0.5 pl-12" data-testid="aviso-associacao-inocua">
                    ⚠️ associação inócua — {l.motivoInocua}
                  </p>
                )}
              </div>
            ))}
          </div>

          {rateio.kgSemVariedade > 0 && (
            <p className="text-[11px] text-atencao mt-2" data-testid="aviso-kg-sem-variedade">
              ⚠️ {fmtKg(rateio.kgSemVariedade)} de produto sem variedade cadastrada — fora do rateio.
            </p>
          )}
        </div>
      )}

      {/* ⚠️ O MESMO diálogo de "Recebi" do Mercado, não uma segunda confirmação:
          um campo (a data), dois botões. Era um confirm seco que gravava `paga_em`
          com o instante do clique — e "Recebido no mês" soma por `paga_em`. `via`
          manda pela rota do Cacho, que existe sem o módulo Mercado ligado. */}
      {confirmarPagoOpen && (
        <DialogRecebi
          via="cacho"
          romaneios={[{
            vendaId: venda.id,
            numero: venda.numero ? `${String(venda.numero).padStart(3, "0")}/${venda.ano}` : "—",
            comprador: venda.comprador.nome,
            valor: aReceber,
            dataVenda: venda.data,
          }]}
          inicialId={venda.id}
          onFechar={() => setConfirmarPagoOpen(false)}
        />
      )}
      {formalizarOpen && (
        <ModalFormalizar
          vendaId={venda.id}
          compradorNome={venda.comprador.nome}
          telefoneCadastro={venda.comprador.telefoneWhatsapp ?? venda.comprador.telefone}
          vendaPaga={venda.status === "PAGA"}
          onFechar={() => setFormalizarOpen(false)}
          onPronto={() => {
            setFormalizarOpen(false);
            toast({ title: "Romaneio formalizado!", description: `Se ${venda.comprador.nome} não tem o Rizoma, mande o link pelo WhatsApp.` });
          }}
        />
      )}
      {naoRecebiOpen && pagoPeloComprador && (
        <DialogNaoRecebi
          pagamento={pagoPeloComprador}
          compradorNome={venda.comprador.nome}
          onFechar={() => setNaoRecebiOpen(false)}
        />
      )}
      {emitirConfirmarOpen && (
        <DialogEmitirEConfirmar
          compradorNome={venda.comprador.nome}
          dataVenda={venda.data}
          pendente={emitirEConfirmar.isPending}
          erro={erroEmissao}
          onEmitir={v => handleEmitirEConfirmar(v)}
          onFechar={() => { setEmitirConfirmarOpen(false); setErroEmissao(null); }}
        />
      )}
    </div>
  );
}
